import React, { FC, useRef, useState, useEffect, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import withStyles from 'src/common/withStyles';
import { useSelector } from 'react-redux';
import Downshift from 'downshift';
import { debounce } from 'lodash';
import { trackEvent } from '../../tracking/facebook';
import gtag from '../../gtag';

import s from './SearchBar.module.scss';

import { sharedMediaQueries, useMediaQuery } from '../../hooks';
import { decorateComponent, getClassNameFor } from '../../helpers';
import autocompleteProvider from '../../utils/autocompleteProvider';
import { withErrorBoundary } from '../ErrorBoundary';
import placeIdSearch from '../../utils/placeIdSearch';
import Svg from '../Svg';
import {
  AutocompletePrediction,
  AutocompleteService,
  AutocompleteSessionToken,
} from '../../types/helpers/googleMapHelpers';
import { ReduxState } from '../../../types/reduxState';
import { NEAR_ME, EXTERNAL_PATHS } from '../../constants';

const isEstablishment = <T extends { types: string[] }>(place: T) => place.types.length === 1 && place.types.includes('establishment');

const filterPlaces = <T extends { types: string[] }>(places: T[]): T[] => {
  if (Array.isArray(places)) {
    return places.filter((place) => !isEstablishment(place));
  }
  return [];
};

const searchByPlaceId = async (payload: Payload) => {
  try {
    const searchRoute = await placeIdSearch(payload);
    if (!searchRoute) {
      return '';
    }
    // Send adwords conversion for searching
    gtag('event', 'conversion', { send_to: 'AW-991706362/NiIJCOqk4cUBEPr58NgD' });

    const base = window.location.href;
    const { searchParams } = new URL(base);
    const url = new URL(searchRoute, base);
    url.searchParams.delete('bareboat');
    url.searchParams.delete('captained');
    if (searchParams.has('currency')) {
      url.searchParams.append('currency', searchParams.get('currency') as string);
    }
    if (url.searchParams.has('place_id') || url.searchParams.has('location')) {
      url.searchParams.delete('swlon');
      url.searchParams.delete('swlat');
      url.searchParams.delete('nelon');
      url.searchParams.delete('nelat');
      url.searchParams.delete('clon');
      url.searchParams.delete('clat');
    }
    return url.toString();
  } catch (error) {
    return '';
  }
};

const defaultPlaces = [
  {
    description: 'Near Me',
    place_id: NEAR_ME,
    types: [],
  },
];

type Payload = {
  placeId: string;
  currency?: string;
};

type SearchBarProps = {
  classNameModifier?: string;
  getAutocompleteService: () => Promise<{
    autocompleteService: AutocompleteService;
    sessionToken: AutocompleteSessionToken;
  }>;
};

const SearchBar: FC<SearchBarProps> = ({ classNameModifier, getAutocompleteService }) => {
  const isOwner = useSelector<ReduxState, boolean | undefined>(
    ({ user }) => user.get('is_owner', false),
  );
  const currencyCode = useSelector<ReduxState, string | undefined>(
    ({ requestCurrency }) => requestCurrency.code,
  );

  const [queryTerm, setQueryTerm] = useState('');
  const [places, setPlaces] = useState<AutocompletePrediction[]>(defaultPlaces);
  const [loading, setLoading] = useState(false);

  const isFixed = classNameModifier && classNameModifier.includes('fixed');
  const iconModifier = isFixed ? 'pin' : 'magnifier';
  const listModifier = isFixed ? 'toTop' : 'toBottom';
  const isLargeEnoughScreen = useMediaQuery(sharedMediaQueries.xl);

  const inputRef = useRef<HTMLInputElement>(null);

  const placeholder = isLargeEnoughScreen ? 'Search for a destination' : 'Search';

  const queryPlaces = useCallback(
    (value: string) => {
      setQueryTerm(value);
      if (value) {
        getAutocompleteService().then(({ autocompleteService, sessionToken }) => {
          autocompleteService.getPlacePredictions(
            {
              input: value,
              sessionToken,
            },
            (predictions, status) => {
              if (status !== google.maps.places.PlacesServiceStatus.OK) {
                setPlaces(defaultPlaces);
                return;
              }
              const allPlaces: AutocompletePrediction[] = [...filterPlaces(predictions || [])];
              setPlaces(allPlaces);
            },
          );
        });
      } else {
        setPlaces(defaultPlaces);
      }
    },
    [getAutocompleteService],
  );

  const debouncedQueryPlaces = useMemo(() => debounce(queryPlaces, 200), [queryPlaces]);

  const handleChange = useCallback(
    async (selection: AutocompletePrediction) => {
      if (!selection) {
        return;
      }

      if (selection.place_id === NEAR_ME) {
        gtag('event', 'Near Me Option', {
          event_category: 'Search',
          event_label: 'Clicked',
        });
        window.location.href = EXTERNAL_PATHS.SEARCH;
        return;
      }

      const payload: Payload = { placeId: selection.place_id };
      if (currencyCode) {
        payload.currency = currencyCode;
      }

      setLoading(true);
      // Send FB conversion for searching
      trackEvent('Search', {
        search_string: queryTerm,
        content_ids: [selection.place_id],
        description: selection.description,
      });
      if (typeof window !== 'undefined' && window.pintrk) {
        window.pintrk('track', 'search', {
          search_query: selection.description,
        });
      }

      const url = await searchByPlaceId(payload);
      if (url) {
        window.location.assign(url.toString());
      }
      setLoading(false);
    },
    [currencyCode, queryTerm],
  );

  useEffect(
    () => () => {
      debouncedQueryPlaces.cancel();
    },
    [debouncedQueryPlaces],
  );

  return (
    <Downshift
      onChange={(selection) => handleChange(selection)}
      itemToString={(item) => (item ? item.description : '')}
      onInputValueChange={debouncedQueryPlaces}
      // This makes the first autocomplete option selected by default, which helps
      // the search button behave sanely.
      initialHighlightedIndex={0}
      id="search-dropdown"
    >
      {({
        getInputProps,
        getItemProps,
        getLabelProps,
        getMenuProps,
        openMenu,
        isOpen,
        highlightedIndex,
        selectHighlightedItem,
        setState,
      }) => (
        <div className={getClassNameFor(s, 'root', classNames(classNameModifier, { isOpen }))}>
          {/* getLabelProps supplies the htmlFor */}
          {/* eslint-disable jsx-a11y/label-has-for */}
          {/* eslint-disable jsx-a11y/label-has-associated-control */}
          <label {...getLabelProps()}>
            {loading && !isFixed ? (
              <div className={s.loadingSpinner} />
            ) : (
              <Svg
                href={isFixed ? '#location-pin' : '#search-icon'}
                className={getClassNameFor(s, 'icon', iconModifier)}
              />
            )}
          </label>
          {/* eslint-enable jsx-a11y/label-has-for */}
          {/* eslint-enable jsx-a11y/label-has-associated-control */}

          {/* form element wrapping the input creates blue GO button on mobile keyboards */}
          <form
            action="#"
            className={s.formWrapper}
            onSubmit={(event) => event.preventDefault()}
          >
            <input
              ref={inputRef}
              {...getInputProps({
                placeholder,
                autoCorrect: 'off',
                autoComplete: 'off',
                spellCheck: 'false',
                className: getClassNameFor(s, 'input', classNames({ isOwner })),
                onFocus: () => {
                  if (places.length > 0) {
                    openMenu();
                  }
                  getAutocompleteService();
                },
              })}
              data-test="SearchInput"
            />
            {(isOpen || !isFixed) && (
              <button
                type="button"
                onClick={({ currentTarget }) => {
                  setState({
                    inputValue: '',
                    isOpen: false,
                    selectedItem: null,
                  });
                  // Blur the button so .formWrapper:focus-within
                  // selector doesn't match anymore to "close" the form
                  currentTarget.blur();
                }}
                className={s.cancel}
              >
                <Svg
                  href="#decline-icon"
                  className={s.cancelIcon}
                />
              </button>
            )}
          </form>
          {isFixed && (
            <button
              type="button"
              className={getClassNameFor(s, 'button', classNames({ loading }))}
              // This makes the button behave basically the same as
              // hitting the enter key
              onClick={() => selectHighlightedItem()}
            >
              {loading ? (
                'Searching...'
              ) : (
                <>
                  Search
                  <Svg
                    href="#next-button"
                    className={s.buttonArrow}
                  />
                </>
              )}
            </button>
          )}

          <ul
            {...getMenuProps({
              className: getClassNameFor(s, 'list', listModifier),
            })}
          >
            {isOpen
              && places
              && places.map((place, index) => (
                <li
                  {...getItemProps({
                    key: place.place_id,
                    item: place,
                    className: getClassNameFor(
                      s,
                      'listItem',
                      classNames({ active: highlightedIndex === index }),
                    ),
                  })}
                >
                  <span className={s.iconCircle}>
                    <Svg
                      href={place.place_id === NEAR_ME ? '#near-me-pin' : '#search-location-pin'}
                      className={getClassNameFor(s, 'icon', 'pin stroked')}
                    />
                  </span>
                  {place.description}
                </li>
              ))}
          </ul>
        </div>
      )}
    </Downshift>
  );
};

const decorators = [withStyles(s), autocompleteProvider, withErrorBoundary(() => null)];

export default decorateComponent(SearchBar, decorators);
